import { useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { Download, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { formatBrDate } from '@/components/super-admin/badges/format'
import type { CertificateData } from '@/services/certificates.service'

// Tamanho fixo do certificado (A4 paisagem ~150dpi).
const CERT_WIDTH = 1240
const CERT_HEIGHT = 877

interface CertificateRendererProps {
  data: CertificateData
  /** Nome do arquivo no download. Default: "certificado-<curso>.pdf" */
  filename?: string
  /** Mostra o botão "Baixar certificado (PDF)". Default: true.
   *  A pré-visualização do super_admin passa false (aluno fake). */
  showDownload?: boolean
}

/**
 * Renderiza o certificado em DOM (1240×877 fixo) e baixa como PDF via
 * html2canvas + jsPDF. Layout absoluto posicionado sobre o background
 * (template escuro AISYM) uploadado pelo super_admin / admin da empresa.
 *
 * IMPORTANTE — CORS de imagens: html2canvas precisa que as imagens
 * referenciadas tenham CORS habilitado (servidor PHP/Apache já manda
 * Access-Control-Allow-Origin pra origins permitidas). Usa
 * `useCORS: true` na chamada.
 *
 * Fundo é escuro — todos os textos são brancos, fonte Poppins (o nome
 * do aluno em Poppins Bold).
 *
 * Comportamento:
 * - data.backgroundUrl null → mostra fundo cinza com aviso "template
 *   não configurado" (mas ainda renderiza nome/curso)
 * - data.signature.company ausente → não mostra bloco da empresa
 * - data.signature.global ausente → idem global
 * - Quando 2 assinaturas: lado a lado; quando 1: centralizada
 */
export function CertificateRenderer({
  data,
  filename,
  showDownload = true,
}: CertificateRendererProps) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const [downloading, setDownloading] = useState(false)
  const { toast } = useToast()

  const hasCompany = !!data.signature.company
  const hasGlobal = !!data.signature.global

  async function handleDownload() {
    if (!canvasRef.current) return
    setDownloading(true)
    try {
      // Garante o Poppins carregado antes da captura — senão o
      // html2canvas pega a fonte fallback.
      await document.fonts.ready

      const canvas = await html2canvas(canvasRef.current, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        scale: 2, // resolução 2x pra qualidade no PDF
      })

      // jsPDF carregado sob demanda — não pesa o bundle inicial.
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [CERT_WIDTH, CERT_HEIGHT],
      })
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        0,
        0,
        pdf.internal.pageSize.getWidth(),
        pdf.internal.pageSize.getHeight()
      )
      pdf.save(filename || `certificado-${data.courseTitle.replace(/\s+/g, '-').toLowerCase()}.pdf`)
      setDownloading(false)
    } catch (err: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro ao gerar certificado',
        description: err instanceof Error ? err.message : 'Tente novamente',
      })
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Preview centralizado, com scroll horizontal em mobile.
          O elemento DENTRO (canvasRef) tem tamanho FIXO 1240×877 — não
          pode escalar via CSS responsivo senão html2canvas pega o tamanho
          escalado e quebra a renderização. Usamos wrapper overflow-auto. */}
      <div className="overflow-auto rounded-lg border border-border/40 bg-muted/20 p-4">
        <Card
          ref={canvasRef}
          className="relative bg-white text-black mx-auto"
          style={{ width: CERT_WIDTH, height: CERT_HEIGHT }}
        >
          {/* Background (template) */}
          {data.backgroundUrl ? (
            <img
              src={data.backgroundUrl}
              alt=""
              crossOrigin="anonymous"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100">
              <p className="text-sm text-muted-foreground">
                Template não configurado — peça ao admin pra subir o fundo
              </p>
            </div>
          )}

          {/* Logo da empresa (reusa companies.logo) — canto superior esquerdo */}
          {data.companyLogoUrl && (
            <img
              src={data.companyLogoUrl}
              alt=""
              crossOrigin="anonymous"
              className="absolute top-10 left-12 z-10"
              style={{ maxHeight: 56, maxWidth: 180, objectFit: 'contain' }}
            />
          )}

          {/* Camada de texto sobre o background — Poppins, texto branco */}
          <div className="relative w-full h-full font-sans flex flex-col justify-center px-24">
            <p
              className="text-center uppercase tracking-[0.3em] text-sm font-semibold text-white mt-32"
              style={{ letterSpacing: '0.3em' }}
            >
              Certificado de Conclusão
            </p>
            <p className="text-center text-base text-white mt-10">Certificamos que</p>
            <h1
              className="text-center font-bold text-white mt-3 leading-tight"
              style={{ fontSize: '56px' }}
            >
              {data.userName}
            </h1>
            <p className="text-center text-base text-white mt-6 leading-relaxed max-w-3xl mx-auto">
              concluiu com êxito o curso
            </p>
            <h2 className="text-center font-semibold text-white mt-2" style={{ fontSize: '32px' }}>
              {data.courseTitle}
            </h2>
            <p className="text-center text-sm text-white mt-4">
              Concluído em {formatBrDate(data.completedAt)}
            </p>

            {/* Badges no canto inferior direito (espelhando o logo da empresa
                no canto superior esquerdo). Imagem-only — o desenho da badge
                já traz o nome embutido. Nome textual continua no DOM com
                `sr-only` pra screen readers e pra cobertura de testes.
                Render só se o back retornou algo (intersect já feito:
                configuradas pela empresa ∩ conquistadas pelo user, com
                iconUrl). Múltiplas badges empilham horizontalmente, da
                direita pra esquerda (a mais recente fica grudada no canto). */}
            {data.badges.length > 0 && (
              <div
                data-testid="certificate-badges"
                className="absolute flex flex-row-reverse items-end gap-3"
                style={{ right: 48, bottom: 48 }}
              >
                {data.badges.map((b) => (
                  <div key={b.id} className="flex flex-col items-center">
                    <img
                      src={b.iconUrl}
                      alt={b.name}
                      crossOrigin="anonymous"
                      style={{ width: 96, height: 96, objectFit: 'contain' }}
                    />
                    <span className="sr-only">{b.name}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Assinaturas — só renderiza o que tem */}
            <div
              className={`flex absolute bottom-32 left-24 right-24 ${
                hasCompany && hasGlobal ? 'justify-between' : 'justify-center'
              }`}
            >
              {hasCompany && (
                <SignatureBlock
                  url={data.signature.company!.url}
                  name={data.signature.company!.name}
                  role={data.signature.company!.role}
                />
              )}
              {hasGlobal && (
                <SignatureBlock
                  url={data.signature.global!.url}
                  name={data.signature.global!.name}
                  role={data.signature.global!.role}
                />
              )}
            </div>
          </div>
        </Card>
      </div>

      {showDownload && (
        <div className="flex justify-center">
          <Button onClick={handleDownload} disabled={downloading} className="gap-2">
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Baixar certificado (PDF)
          </Button>
        </div>
      )}
    </div>
  )
}

function SignatureBlock({ url, name, role }: { url: string; name: string; role?: string | null }) {
  return (
    <div className="flex flex-col items-center gap-2 min-w-[280px]">
      <img
        src={url}
        alt=""
        crossOrigin="anonymous"
        style={{ maxHeight: 80, maxWidth: 280, objectFit: 'contain' }}
      />
      <div className="border-t border-white/50 w-full pt-1.5">
        <p className="text-center text-sm font-medium text-white">{name}</p>
        {role && <p className="text-center text-xs text-white/70">{role}</p>}
      </div>
    </div>
  )
}
